/**
 * useNoticeList — the facilitator's notification list and the bell's count
 * (Design's sketch, Nino OK 12 Oct). Refreshed every 30 s while Studio is in
 * front, also during a drill (the bell count still rises there; only pop-ups
 * wait), and whenever the list is opened.
 */
import { useCallback, useEffect, useState } from 'react';
import { notificationApi } from '@/lib/api';
import { NOTICE_POLL_MS, type Notice } from '@/lib/notices';

export interface NoticeList {
  items: Notice[];
  unread: number;
  /** False until the first answer; the list then shows what it has. */
  loaded: boolean;
  /** The last refresh failed: the list says so instead of looking empty. */
  failed: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  refresh: () => Promise<void>;
  markRead: (ids: string[]) => void;
  markAllRead: () => void;
}

export function useNoticeList(enabled: boolean): NoticeList {
  const [items, setItems] = useState<Notice[]>([]);
  const [unread, setUnread] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const r = await notificationApi.list({ limit: 30 });
      setItems(r.items);
      setUnread(r.unread_count);
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoaded(true);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void refresh(); }, NOTICE_POLL_MS);
    const onShow = () => { if (document.visibilityState === 'visible') void refresh(); };
    document.addEventListener('visibilitychange', onShow);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', onShow); };
  }, [enabled, refresh]);

  useEffect(() => { if (open) void refresh(); }, [open, refresh]);

  const markRead = useCallback((ids: string[]) => {
    const fresh = ids.filter((id) => items.some((n) => n.id === id && !n.readAt));
    if (fresh.length === 0) return;
    const at = new Date().toISOString();
    setItems((xs) => xs.map((n) => (fresh.includes(n.id) ? { ...n, readAt: at } : n)));
    setUnread((u) => Math.max(0, u - fresh.length));
    // Best effort: if the server refuses, the next refresh shows it unread again, which is the true state.
    fresh.forEach((id) => { void notificationApi.markRead(id).catch(() => undefined); });
  }, [items]);

  const markAllRead = useCallback(() => {
    const at = new Date().toISOString();
    setItems((xs) => xs.map((n) => (n.readAt ? n : { ...n, readAt: at })));
    setUnread(0);
    void notificationApi.markAllRead().catch(() => { void refresh(); });
  }, [refresh]);

  return { items, unread, loaded, failed, open, setOpen, refresh, markRead, markAllRead };
}
